/**
 * adaptOrder(enrichedOrder) → NormalizedOrder
 *
 * Extracts nested fields into flat top-level properties.
 * This is the ONLY client-side code that reads orderLegCollection.
 * Child orders already have flat fields (enrichOrder() extracts them server-side).
 */
export function adaptOrder(enrichedOrder) {
  if (!enrichedOrder) return null
  const leg = enrichedOrder.orderLegCollection?.[0]
  const instrument = leg?.instrument || {}

  return {
    orderId: String(enrichedOrder.orderId || ''),
    accountNumber: enrichedOrder.accountNumber || '',
    parentOrderId: enrichedOrder.parentOrderId ?? null,

    symbol: (instrument.symbol || enrichedOrder.symbol || '').toUpperCase(),
    assetType: instrument.assetType || 'EQUITY',
    side: leg?.instruction || enrichedOrder.side || enrichedOrder.instruction || '',
    orderType: enrichedOrder.orderType || '',

    price: enrichedOrder.price ?? null,
    stopPrice: enrichedOrder.stopPrice ?? null,
    entryPrice: enrichedOrder.entryPrice ?? null,
    computedStopPrice: enrichedOrder.computedStopPrice ?? null,

    quantity: enrichedOrder.quantity || enrichedOrder.remainingQuantity || 0,
    remainingQuantity: enrichedOrder.remainingQuantity ?? null,

    status: enrichedOrder.status || '',
    cancelable: enrichedOrder.cancelable || false,
    editable: enrichedOrder.editable || false,

    isBracket: !!enrichedOrder.hasChildren,
    childOrders: (enrichedOrder.childOrders || []).map(child => {
      const childLeg = child.orderLegCollection?.[0]
      return {
        orderId: String(child.orderId || ''),
        orderType: child.orderType || '',
        status: child.status || '',
        stopPrice: child.stopPrice ?? null,
        price: child.price ?? null,
        quantity: child.quantity ?? child.remainingQuantity ?? childLeg?.quantity ?? 0,
        remainingQuantity: child.remainingQuantity ?? null,
        computedStopPrice: child.computedStopPrice ?? null,
        side: child.instruction || child.side || childLeg?.instruction || '',
        symbol: (child.symbol || childLeg?.instrument?.symbol || instrument.symbol || '').toUpperCase(),
        duration: child.duration || '',
        cancelable: child.cancelable || false,
        editable: child.editable || false,
      }
    }),
    bracketMaxLoss: enrichedOrder.bracketMaxLoss ?? null,

    duration: enrichedOrder.duration || '',
    promotedFromBracket: enrichedOrder.promotedFromBracket || false,

    _raw: enrichedOrder,
  }
}

/**
 * adaptOrders(enrichedOrders) → NormalizedOrder[]
 */
export function adaptOrders(enrichedOrders) {
  if (!enrichedOrders || !Array.isArray(enrichedOrders)) return []
  return enrichedOrders.map(adaptOrder).filter(Boolean)
}
